import React, {useImperativeHandle, forwardRef, useRef, useContext, useEffect} from "react";
import {Row, Col, Spin} from 'antd';
import {ExclamationCircleOutlined, CloseOutlined} from "@ant-design/icons";
import classNames from 'classnames';

const Context = React.createContext({wrapDom: null});
const Provider = Context.Provider;

// 最外层包裹容器
const Wrap = forwardRef((props, ref) => {
    const {children, theme} = props;
    const dom = useRef(null);

    useImperativeHandle(ref, () => ({
        dom: dom.current
    }));

   return (
       <Provider
           value={{
               wrapDom: dom.current,
           }}
       >
           <div ref={dom} className={classNames('cesium-popup', { [`cesium-popup-${theme}`]: theme })}>
               {children}
           </div>
       </Provider>
   )
})

// 关闭按钮
const Close = (props) => {
    const {onClick} = props
    return (
        <CloseOutlined
            className={classNames('cesium-popup-close')}
            width={16}
            height={16}
            onClick={onClick}
        />
    )
}

// 标题
const Title = (props) => {
    const {title} = props;

    return (
        <div
            title={title}
            className={classNames('cesium-popup-title', { 'cesium-popup-noTitle': !title })}
        >
            {title}
        </div>
    )
}

// 内容
const Content = (props) => {
    const {list, gutter, loading = false, children, width, column = 1, style = {}} = props;

    const {wrapDom} = useContext(Context);

    const _style = {
        width: '100%',
        ...style
    }

    useEffect(() => {
        return () => {
            wrapDom.style.setProperty('--popup-width', '250px');
        }
    }, [])

    if (width) {
        _style.width = width;
        wrapDom.style.setProperty('--popup-width', `${_style.width}px`);
    } else if (column >= 2) {
        _style.width = column * 200;
        wrapDom.style.setProperty('--popup-width', `${_style.width}px`);
    }

    return (
        <Spin spinning={loading}>
            <div style={_style}>
                <Row gutter={gutter || [5, 5]}>
                    {list?.length > 0 &&
                        list.map((item, index) => (
                            <Col key={index} span={item.span || (24 / column)}>
                                <div className={classNames('cesium-popup-content-item')} onClick={item.onClick}>
                                    {item.label &&
                                        <div
                                            className={classNames('cesium-popup-content-item-label')}
                                        >
                                            {item.label}：
                                        </div>
                                    }

                                    {((item.value || String(item.value) === '0') || item.unit) &&
                                        <div
                                            className={classNames('cesium-popup-content-item-value')}
                                            style={{
                                                color: item.onAlarm ? 'rgb(245,13,13)' : '#FFF',
                                                ...(item.style || {})
                                            }}
                                            title={item.value}
                                        >
                                            {item.value}{item.unit}
                                        </div>
                                    }

                                    {item.content}
                                </div>
                            </Col>
                        ))}
                </Row>

                {children}
            </div>
        </Spin>
    )
}

// 底部按钮
const Footer = (props) => {
    const {list} = props;

    if (list?.length === 0) {
        return <></>
    }

    return (
        <div className={classNames("cesium-popup-footer")}>
            {list.map((item, index) => (
                <div key={index} className={classNames("cesium-popup-footer-item")} onClick={item.onClick}>
                    {item.icon && <span className={classNames("cesium-popup-footer-item-icon")}>{item.icon}</span>}

                    {item.label}
                </div>
            ))}
        </div>
    )
}

// 模版
const Template = (props) => {
    const {
        loading, // 加载中
        contentList, // 内容列表，[{label: '', value: ''}]
        contentStyle, // 内容样式
        content, // 自定义内容
        otherContent, // 其他内容
        gutter, // 内容列间距
        column, // 内容列数
        footerList, // 底部按钮列表，[{label: '', icon: '', onClick: () => {}}]
        footer, // 自定义底部内容
        detail, // 详情按钮
        width, // 自定义容器宽度
        children // 自定义内容
    } = props;

    // 底部按钮
    const footerButtons = [
        ...(detail?.show ? [
            {
                label: "详情",
                icon: <ExclamationCircleOutlined />,
                ...detail
            }
        ] : []),
        ...(footerList || [])
    ]

    return (
        <>
            {content ||
                <Content
                    loading={loading}
                    list={contentList}
                    column={column}
                    gutter={gutter}
                    style={contentStyle}
                    width={width}
                >
                    {otherContent}
                </Content>
            }

            {children}

            {footer || <Footer list={footerButtons}/>}
        </>
    )
}

const Popup = forwardRef((props, ref) => {
    const {title, onClose, children, theme} = props;

    const wrapRef = useRef({});

    useImperativeHandle(ref, () => ({
        wrapDom: wrapRef.current.dom
    }));

    return (
        <Wrap ref={wrapRef} theme={theme}>
            <Title title={title}/>
            {onClose && <Close onClick={onClose}/>}

            <div className={classNames('cesium-popup-content')}>
                {children}
            </div>
        </Wrap>
    )
})

Popup.Title = Title;
Popup.Content = Content;
Popup.Footer = Footer;
Popup.Template = Template;
Popup.Context = Context;

export default Popup;
